import React, { useState } from 'react';
import { Search, Plus, Filter, Star, CheckCircle2, Clock, XCircle, Edit2, Trash2, Download, Users, Eye, MoreHorizontal } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { ContactModal } from '../components/ContactModal';
import { Contact } from '../data/workspaceData';
export function Contacts() {
  const {
    contacts,
    currentWorkspace,
    newsletters,
    tags,
    deleteContact
  } = useWorkspace();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Modal states
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [viewingContact, setViewingContact] = useState<Contact | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.email.toLowerCase().includes(searchQuery.toLowerCase()) || `${contact.firstName} ${contact.lastName}`.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || categoryFilter === 'high-profile' && contact.isHighProfile || categoryFilter === 'regular' && !contact.isHighProfile || contact.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || contact.optInStatus === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });
  const confirmedCount = contacts.filter(c => c.optInStatus === 'confirmed').length;
  const pendingCount = contacts.filter(c => c.optInStatus === 'pending').length;
  const highProfileCount = contacts.filter(c => c.isHighProfile).length;
  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactModal(true);
  };
  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setShowContactModal(true);
  };
  const handleViewContact = (contact: Contact) => {
    setViewingContact(contact);
  };
  const handleDeleteContact = (id: number) => {
    deleteContact(id);
    setShowDeleteConfirm(null);
  };
  const getOptInIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" data-id="element-820" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500 flex-shrink-0" data-id="element-821" />;
      case 'unsubscribed':
        return <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" data-id="element-822" />;
      default:
        return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-700';
      case 'pending':
        return 'text-yellow-700';
      case 'unsubscribed':
        return 'text-red-700';
      default:
        return 'text-gray-700';
    }
  };
  return <div className="space-y-6" data-id="element-823">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-824">
        <div className="min-w-0" data-id="element-825">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-826">Contacts</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-827">
            Manage contacts and assign categories for {currentWorkspace.name}.
            Only double opt-in confirmed contacts can receive campaigns.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-828">
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap" data-id="element-829">
            <Download className="h-4 w-4 flex-shrink-0" data-id="element-830" />
            <span data-id="element-831">Export</span>
          </button>
          <button onClick={handleAddContact} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-832">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-833" />
            <span data-id="element-834">Add Contact</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-835">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-836">
          <div className="flex items-center gap-3" data-id="element-837">
            <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-838">
              <Users className="h-5 w-5 text-blue-600" data-id="element-839" />
            </div>
            <div className="min-w-0" data-id="element-840">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-841">
                {contacts.length}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-842">Total Contacts</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-843">
          <div className="flex items-center gap-3" data-id="element-844">
            <div className="p-2 bg-green-50 rounded-lg flex-shrink-0" data-id="element-845">
              <CheckCircle2 className="h-5 w-5 text-green-600" data-id="element-846" />
            </div>
            <div className="min-w-0" data-id="element-847">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-848">
                {confirmedCount}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-849">
                Confirmed (Targetable)
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-850">
          <div className="flex items-center gap-3" data-id="element-851">
            <div className="p-2 bg-yellow-50 rounded-lg flex-shrink-0" data-id="element-852">
              <Clock className="h-5 w-5 text-yellow-600" data-id="element-853" />
            </div>
            <div className="min-w-0" data-id="element-854">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-855">
                {pendingCount}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-856">Pending Opt-in</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-857">
          <div className="flex items-center gap-3" data-id="element-858">
            <div className="p-2 bg-amber-50 rounded-lg flex-shrink-0" data-id="element-859">
              <Star className="h-5 w-5 text-amber-600" data-id="element-860" />
            </div>
            <div className="min-w-0" data-id="element-861">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-862">
                {highProfileCount}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-863">High-Profile</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-864">
        <div className="relative flex-1 min-w-0" data-id="element-865">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-866" />
          <input type="text" placeholder="Search by email or name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-867" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap" data-id="element-868">
          <Filter className="h-4 w-4 text-gray-400 hidden sm:block flex-shrink-0" data-id="element-869" />
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="border border-gray-300 rounded-md text-sm py-2 pl-3 pr-8 focus:ring-blue-500 focus:border-blue-500 flex-shrink-0" data-id="element-870">
            <option value="all" data-id="element-871">All Categories</option>
            <option value="high-profile" data-id="element-872">⭐ High-Profile Only</option>
            <option value="regular" data-id="element-873">Regular Contacts</option>
            <optgroup label="Categories" data-id="element-874">
              {(currentWorkspace.contactCategories || []).map(cat => <option key={cat} value={cat} data-id="element-875">
                  {cat}
                </option>)}
            </optgroup>
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-300 rounded-md text-sm py-2 pl-3 pr-8 focus:ring-blue-500 focus:border-blue-500 flex-shrink-0" data-id="element-876">
            <option value="all" data-id="element-877">All Status</option>
            <option value="confirmed" data-id="element-878">✓ Confirmed</option>
            <option value="pending" data-id="element-879">⏳ Pending</option>
            <option value="unsubscribed" data-id="element-880">✗ Unsubscribed</option>
          </select>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-881">
        <div className="flex items-start gap-3" data-id="element-882">
          <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-883" />
          <div className="text-sm text-blue-800 min-w-0" data-id="element-884">
            <p className="font-medium" data-id="element-885">Double Opt-in Required</p>
            <p className="mt-1" data-id="element-886">
              Only contacts with <strong data-id="element-887">Confirmed</strong> status can be
              included in campaigns. Pending contacts must click the
              confirmation link in their opt-in email first.
            </p>
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-888">
        {filteredContacts.length === 0 ? <div className="p-12 text-center" data-id="element-889">
            <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-890" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-891">
              No contacts found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-892">
              {searchQuery || categoryFilter !== 'all' || statusFilter !== 'all' ? 'Try adjusting your filters.' : 'Add contacts manually or wait for form submissions.'}
            </p>
            <button onClick={handleAddContact} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-893">
              <Plus className="h-4 w-4" data-id="element-894" />
              Add First Contact
            </button>
          </div> : <div className="overflow-x-auto" data-id="element-895">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-896">
              <thead className="bg-gray-50" data-id="element-897">
                <tr data-id="element-898">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-899">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-900">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-901">
                    Opt-in Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-902">
                    Lists
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-903">
                    Source
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-904">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-905">
                {filteredContacts.map(contact => <tr key={contact.id} className="hover:bg-gray-50 group" data-id="element-906">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-907">
                      <div className="flex items-center gap-3" data-id="element-908">
                        <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${contact.isHighProfile ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-909">
                          {contact.isHighProfile ? <Star className="h-4 w-4" data-id="element-910" /> : (contact.firstName?.[0] || contact.email[0]).toUpperCase()}
                        </div>
                        <div className="min-w-0" data-id="element-911">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-912">
                            {contact.firstName && contact.lastName ? `${contact.firstName} ${contact.lastName}` : contact.email}
                          </div>
                          <div className="text-xs text-gray-500 truncate max-w-[200px]" data-id="element-913">
                            {contact.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-914">
                      {contact.category ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 whitespace-nowrap" data-id="element-915">
                          <Star className="h-3 w-3 flex-shrink-0" data-id="element-916" />
                          {contact.category}
                        </span> : <span className="text-xs text-gray-400" data-id="element-917">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-918">
                      <div className="flex items-center gap-2" data-id="element-919">
                        {getOptInIcon(contact.optInStatus)}
                        <span className={`text-sm whitespace-nowrap ${getStatusColor(contact.optInStatus)}`} data-id="element-920">
                          {contact.optInStatus.charAt(0).toUpperCase() + contact.optInStatus.slice(1)}
                        </span>
                      </div>
                      {contact.optInConfirmedAt && <div className="text-xs text-gray-400 mt-0.5" data-id="element-921">
                          {contact.optInConfirmedAt}
                        </div>}
                    </td>
                    <td className="px-6 py-4" data-id="element-922">
                      <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-923">
                        {contact.subscribedLists.length === 0 ? <span className="text-xs text-gray-400" data-id="element-924">None</span> : contact.subscribedLists.slice(0, 2).map(listId => {
                    const list = newsletters.find(n => n.id === listId);
                    return list ? <span key={listId} className="inline-flex px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 whitespace-nowrap" data-id="element-925">
                                {list.name}
                              </span> : null;
                  })}
                        {contact.subscribedLists.length > 2 && <span className="inline-flex px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500" data-id="element-926">
                            +{contact.subscribedLists.length - 2} more
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-927">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${contact.source === 'manual' ? 'bg-purple-100 text-purple-700' : contact.source === 'import' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-928">
                        {contact.source}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-929">
                      <div className="flex items-center justify-end gap-1" data-id="element-930">
                        <button onClick={() => handleViewContact(contact)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded flex-shrink-0" title="View Details" data-id="element-931">
                          <Eye className="h-4 w-4" data-id="element-932" />
                        </button>
                        <button onClick={() => handleEditContact(contact)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded flex-shrink-0" title="Edit Contact" data-id="element-933">
                          <Edit2 className="h-4 w-4" data-id="element-934" />
                        </button>
                        <button onClick={() => setShowDeleteConfirm(contact.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0" title="Delete Contact" data-id="element-935">
                          <Trash2 className="h-4 w-4" data-id="element-936" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Contact Modal (Add/Edit) */}
      <ContactModal isOpen={showContactModal} onClose={() => {
      setShowContactModal(false);
      setEditingContact(null);
    }} contact={editingContact} data-id="element-937" />

      {/* View Contact Detail Modal */}
      {viewingContact && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-938">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-939">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setViewingContact(null)} data-id="element-940" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-941">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-942">
                <div className="flex items-center justify-between" data-id="element-943">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-944">
                    Contact Details
                  </h3>
                  <button onClick={() => setViewingContact(null)} className="text-gray-400 hover:text-gray-500" data-id="element-945">
                    <XCircle className="h-5 w-5" data-id="element-946" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 space-y-4" data-id="element-947">
                <div className="flex items-center gap-4" data-id="element-948">
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center text-lg font-medium ${viewingContact.isHighProfile ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-949">
                    {viewingContact.isHighProfile ? <Star className="h-6 w-6" data-id="element-950" /> : (viewingContact.firstName?.[0] || viewingContact.email[0]).toUpperCase()}
                  </div>
                  <div data-id="element-951">
                    <h4 className="text-lg font-medium text-gray-900" data-id="element-952">
                      {viewingContact.firstName && viewingContact.lastName ? `${viewingContact.firstName} ${viewingContact.lastName}` : viewingContact.email}
                    </h4>
                    <p className="text-sm text-gray-500" data-id="element-953">
                      {viewingContact.email}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100" data-id="element-954">
                  <div data-id="element-955">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-956">
                      Category
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-957">
                      {viewingContact.category ? <span className="inline-flex items-center gap-1" data-id="element-958">
                          <Star className="h-3 w-3 text-amber-500" data-id="element-959" />
                          {viewingContact.category}
                        </span> : 'Regular Contact'}
                    </p>
                  </div>
                  <div data-id="element-960">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-961">
                      Opt-in Status
                    </p>
                    <p className={`mt-1 text-sm flex items-center gap-1 ${getStatusColor(viewingContact.optInStatus)}`} data-id="element-962">
                      {getOptInIcon(viewingContact.optInStatus)}
                      {viewingContact.optInStatus.charAt(0).toUpperCase() + viewingContact.optInStatus.slice(1)}
                    </p>
                  </div>
                  <div data-id="element-963">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-964">
                      Source
                    </p>
                    <p className="mt-1 text-sm text-gray-900 capitalize" data-id="element-965">
                      {viewingContact.source}
                    </p>
                  </div>
                  <div data-id="element-966">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-967">
                      Sub-Region
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-968">
                      {viewingContact.subRegion || 'All Regions'}
                    </p>
                  </div>
                </div>

                {viewingContact.tags.length > 0 && <div className="pt-4 border-t border-gray-100" data-id="element-969">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-970">
                      Tags
                    </p>
                    <div className="flex flex-wrap gap-1" data-id="element-971">
                      {viewingContact.tags.map(tag => <span key={tag} className="inline-flex px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700" data-id="element-972">
                          {tag}
                        </span>)}
                    </div>
                  </div>}

                {viewingContact.subscribedLists.length > 0 && <div className="pt-4 border-t border-gray-100" data-id="element-973">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-974">
                      Subscribed Lists
                    </p>
                    <div className="flex flex-wrap gap-1" data-id="element-975">
                      {viewingContact.subscribedLists.map(listId => {
                  const list = newsletters.find(n => n.id === listId);
                  return list ? <span key={listId} className="inline-flex px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700" data-id="element-976">
                            {list.name}
                          </span> : null;
                })}
                    </div>
                  </div>}

                {viewingContact.notes && <div className="pt-4 border-t border-gray-100" data-id="element-977">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-978">
                      Notes
                    </p>
                    <p className="text-sm text-gray-700" data-id="element-979">
                      {viewingContact.notes}
                    </p>
                  </div>}

                <div className="pt-4 border-t border-gray-100 text-xs text-gray-400" data-id="element-980">
                  <p data-id="element-981">Created: {viewingContact.createdAt}</p>
                  <p data-id="element-982">Updated: {viewingContact.updatedAt}</p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3" data-id="element-983">
                <button onClick={() => setViewingContact(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-984">
                  Close
                </button>
                <button onClick={() => {
              handleEditContact(viewingContact);
              setViewingContact(null);
            }} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-985">
                  Edit Contact
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-986">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-987">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowDeleteConfirm(null)} data-id="element-988" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-989">
              <div className="flex items-center gap-4" data-id="element-990">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-991">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-992" />
                </div>
                <div data-id="element-993">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-994">
                    Delete Contact
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-995">
                    Are you sure you want to delete this contact? This action
                    cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-996">
                <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-997">
                  Cancel
                </button>
                <button onClick={() => handleDeleteContact(showDeleteConfirm)} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-id="element-998">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}